import { Box, Divider, Stack, Typography, styled } from "@mui/material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MyTrainingsViewContext } from "context/myTrainings/MyTrainingsViewContext";
import { LessonProgress } from "models/LessonProgress";
import { Lesson } from "models/Lesson";

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const LessonRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
}));

const LessonName = styled(Typography)({
  flex: 1,
});

const PointsTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
  marginLeft: theme.spacing(1),
}));

const ProgressesBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(-1.8),
  marginLeft: theme.spacing(1.6),
}));

const ProgressRow = ({ lp }: { lp: LessonProgress }) => {
  const { t } = useTranslation();
  const versionLabel = lp.lessonVersion.name
    ? ` (${lp.lessonVersion.name})`
    : ` (v${lp.lessonVersion.version})`;

  return (
    <LessonRow>
      <LessonName variant="body2">
        {lp.lessonVersion.lessonName}
        {versionLabel}
      </LessonName>
      <PointsTypography variant="body2">
        {lp.pointsScored} / {lp.lessonVersion.maxPoints}{" "}
        {t("myTrainings.historicalProgress.pointsShortcut")}
      </PointsTypography>
    </LessonRow>
  );
};

const NotStartedRow = ({ lesson }: { lesson: Lesson }) => (
  <LessonRow>
    <LessonName variant="body2">{lesson.name}</LessonName>
    <PointsTypography variant="body2">—</PointsTypography>
  </LessonRow>
);

export const MobileHomeLessonComponent = () => {
  const { t } = useTranslation();
  const { trainingStatistics, isStatisticOfLoggedUser } = useContext(
    MyTrainingsViewContext,
  );

  if (!trainingStatistics){
    return null;
  }

  const { actualProgresses, lessons } = trainingStatistics;

  const notStarted = useMemo(
    () =>
      lessons.filter(
        (lesson) =>
          !actualProgresses.find((lp) => lp.lessonVersion.lessonId === lesson.id),
      ),
    [lessons, actualProgresses],
  );

  if (!!actualProgresses.length && !!notStarted.length){
    return null;
  }

  return (
    <Stack>
      <SectionTitle variant="subtitle1">
        {isStatisticOfLoggedUser
          ? t("myTrainings.yourLessons")
          : t("myTrainings.userLessons")}
      </SectionTitle>

      <ProgressesBox>
        {actualProgresses.map((lp, index) => (
          <Stack key={lp.id}>
            <ProgressRow lp={lp} />
            {index < actualProgresses.length - 1 || notStarted.length > 0}
          </Stack>
        ))}
      </ProgressesBox>

      {notStarted.map((lesson, index) => (
        <Stack key={lesson.id}>
          <NotStartedRow lesson={lesson} />
          {index < notStarted.length - 1 && <Divider />}
        </Stack>
      ))}
    </Stack>
  );
};
